import React from 'react';
import useTimelineRegistryHelper from '../timelineRegistryHelper';
import TimelineRecord from '../TimelineRecordWrapper';
import TimelinePagingHandler from '../paging/TimelinePagingHandler';
import { useListContext } from 'framework/list';
import { TimelineContextData } from '../Timeline';
import { useShowContext } from 'framework/show';
import type { Data } from 'framework/types';
import type { TimelineItem, TimelineItemId } from '../types';
import { useUiTheme } from 'framework/uiTheme';
import { Alert as DefaultAlert, Loading as DefaultLoading } from 'framework/ui/mui-builder';
import TimelineList from './TimelineList';

const DEFAULT_FUNCTION = () => {};

interface TimelineLayoutProps {
  noEntriesMessage: string;
}

const TimelineLayout = ({ noEntriesMessage = 'No entries in this timeline yet.' }: TimelineLayoutProps) => {
  const { Alert: ThemedAlert, Loading: ThemedLoading } = useUiTheme();
  const Alert = ThemedAlert || DefaultAlert;
  const Loading = ThemedLoading || DefaultLoading;
  const {
    data: { items = [] } = {},
    registries,
    currentEditingId,
    setCurrentEditingId = DEFAULT_FUNCTION,
    updateInList = DEFAULT_FUNCTION,
    wrapItem = () => ({}),
    loading,
    addForm,
    isHighlighted = () => false,
  }: TimelineContextData = useListContext();
  const { getRegistry } = useTimelineRegistryHelper(registries || []);
  const { data: parentContext } = useShowContext();

  const clearEditing = () => setCurrentEditingId(null);

  const renderUpdateForm = (
    UpdateFormTemplate: React.ReactElement,
    timelineItemEntity: TimelineItem,
    source: string,
    hasFullData: boolean,
    additionalAfterUpdateSubmit?: (arg: TimelineItem, parentContext?: Data) => void,
  ) => (
    // @ts-ignore
    <UpdateFormTemplate
      entityId={timelineItemEntity?.id}
      afterSubmit={{
        execute: (id: TimelineItemId, data: TimelineItem) => {
          updateInList(timelineItemEntity?.timelineId || null, hasFullData ? data : wrapItem(data, source));
          clearEditing();
          if (additionalAfterUpdateSubmit) {
            additionalAfterUpdateSubmit(data, parentContext);
          }
        },
      }}
      key={`form-${timelineItemEntity?.timelineId}`}
      getDataFunc={() => timelineItemEntity}
      onCancel={clearEditing}
    />
  );

  const renderLayout = (
    UpdateForm: React.ReactElement,
    RecordLayout: React.ReactElement,
    timelineItemEntity: TimelineItem,
    source: string,
    hasFullData: boolean,
    additionalAfterUpdateSubmit?: (arg: TimelineItem, parentContext?: Data) => void,
    isPinned?: boolean,
  ) => (
    // @ts-ignore
    <RecordLayout
      updateComponent={renderUpdateForm(
        UpdateForm,
        timelineItemEntity,
        source,
        hasFullData,
        additionalAfterUpdateSubmit,
      )}
      isPinned={isPinned}
      isHighlighted={isHighlighted(timelineItemEntity?.timelineId)}
      isEditing={currentEditingId === timelineItemEntity?.timelineId}
      onEdit={() => setCurrentEditingId(timelineItemEntity?.timelineId || null)}
      key={`record-${timelineItemEntity?.timelineId}`}
    />
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      {!items.length && !addForm && (
        <Alert severity="info" sx={{ mt: 3 }}>
          {noEntriesMessage}
        </Alert>
      )}

      <TimelinePagingHandler>
        <TimelineList>
          {addForm}

          {items
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()) // Sort by createdAt
            .sort((a, b) => Number(Boolean(b.isPinned)) - Number(Boolean(a.isPinned))) // Sort by Pinned
            .map((item: TimelineItem) => {
              const {
                UpdateForm = <div />,
                source = '',
                RecordLayout = <div />,
                // @ts-ignore
                hasFullData = false,
                additionalAfterUpdateSubmit,
              } = getRegistry(item) || {};
              // @ts-ignore
              const timelineItemEntity = hasFullData ? item : item && item[source];

              return (
                <React.Fragment key={`${timelineItemEntity.id}`}>
                  <TimelineRecord entity={timelineItemEntity} key={`wrapper-${timelineItemEntity.timelineId}`}>
                    {renderLayout(
                      UpdateForm,
                      RecordLayout,
                      timelineItemEntity,
                      source,
                      hasFullData,
                      additionalAfterUpdateSubmit,
                      item?.isPinned,
                    )}
                  </TimelineRecord>
                </React.Fragment>
              );
            })}
        </TimelineList>
      </TimelinePagingHandler>
    </>
  );
};

export default TimelineLayout;
